import { Link } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Video } from "lucide-react";
import { usePublicVideos } from "../hooks/use-public-videos";
import { useDeletePublicVideo } from "../hooks/use-delete-public-video";
import { PublicVideoCard } from "./public-video-card";

/** Full-screen public clip feed, opened from the workout screen. */
export function PublicVideosPage() {
  const { data: videos, isLoading, isError, refetch } = usePublicVideos();
  const deleteMutation = useDeletePublicVideo();

  const handleDelete = (videoId: string) => {
    if (window.confirm("Delete this clip? This cannot be undone.")) {
      deleteMutation.mutate(videoId);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gradient-to-b from-[#17193b] to-black text-white">
      <div className="mx-auto max-w-5xl px-4 py-6">
        <header className="mb-6 flex items-center gap-4">
          <Link
            to="/"
            className="flex size-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Back to workout"
          >
            <ArrowLeft className="size-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-light tracking-tight">Public Videos</h1>
            <p className="text-xs text-white/50">Clips posted by the community</p>
          </div>
        </header>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-sm text-white/60">
            <Loader2 className="size-4 animate-spin" />
            Loading clips…
          </div>
        ) : isError ? (
          <div className="rounded-xl border border-white/10 bg-white/5 p-10 text-center">
            <p className="text-sm text-white/70">Couldn&apos;t load the public feed.</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-4 rounded-lg bg-lb-blue px-4 py-2 text-sm font-semibold text-white"
            >
              Try again
            </button>
          </div>
        ) : (videos ?? []).length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/20 bg-white/5 p-12 text-center">
            <Video className="mx-auto size-10 text-white/30" />
            <p className="mt-3 text-sm text-white/70">No public clips yet.</p>
            <p className="mt-1 text-xs text-white/45">
              Record a clip during a workout and post it to start the feed.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(videos ?? []).map((video) => (
              <PublicVideoCard
                key={video.id}
                video={video}
                onDelete={video.mine ? () => handleDelete(video.id) : undefined}
                deleting={deleteMutation.isPending && deleteMutation.variables === video.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
